import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  StarOutlined,
} from "@ant-design/icons";
import videoService from "../../../services/videoService";
import { useCommon } from "../../../context/CommonContext";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";

const { Title } = Typography;
const { Option } = Select;

const AddRating = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await videoService.addVideoRating(values);
      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Rating added successfully",
        });
        form.resetFields();
        navigate("/ratings");
      } else {
        notify.error({
          message: response.data?.message || "Failed to add rating",
        });
      }
    } catch (error) {
      console.error("Error adding rating:", error);
      notify.error({ message: "Error adding rating" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({ is_parental: "N", vr_is_active: "Y" });
  };

  const handleGoBack = () => {
    navigate("/ratings");
  };

  useEffect(() => {
    setPageTitle("Add Video Rating");
  }, [setPageTitle]);

  return (
    <div className="min-h-screen">
      <Card>
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          initialValues={{ is_parental: "N", vr_is_active: "Y" }}
          className="space-y-6"
        >
          {/* Rating Information Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <StarOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Rating Information
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Rating Name
                    </span>
                  }
                  name="rating_name"
                  rules={[
                    { required: true, message: "Please enter rating name" },
                    { validator: noWhitespaceValidator },
                  ]}
                >
                  <Input
                    placeholder="Enter rating name"
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Is Parental Control
                    </span>
                  }
                  name="is_parental"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="warning" text="Yes" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="No" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Status
                    </span>
                  }
                  name="vr_is_active"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="success" text="Active" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="Inactive" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col xs={24}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Rating Description
                    </span>
                  }
                  name="vr_description"
                  rules={[{ validator: noWhitespaceValidator }]}
                >
                  <Input.TextArea
                    placeholder="Enter rating description"
                    rows={4}
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div
            className="flex justify-end gap-4 pt-6 border-t"
            style={{ borderColor: "#d9d9d9" }}
          >
            <Space size="middle">
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
                size="large"
                className="px-6"
              >
                Reset
              </Button>
              <Button
                onClick={handleGoBack}
                icon={<ArrowLeftOutlined />}
                size="large"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="px-8"
              >
                {loading ? "Adding..." : "Add Rating"}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AddRating;
